import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { localizeAssets, type PageContent, type PageTheme } from '../model/pages';
import type { PreviewForm } from '../render/FormMock';
import PageView from '../render/PageView';
import { ArrowLeftIcon } from '../icons';
import IframePreview from './IframePreview';
import DesktopPreview from './DesktopPreview';

/** What the full preview needs from a template (a starter or a saved one). */
export interface PreviewTemplate {
  name: string;
  description?: string;
  content: PageContent;
  theme: PageTheme;
  /** A still of the form the template creates (FormMock), or null. */
  previewForm?: PreviewForm | null;
}

const WIDTHS = [['desktop', 'Desktop'], ['mobile', 'Mobile']] as const;

/** Full-screen look at a template before using it: the real page render at
 *  desktop or phone width, every step of a multi-step funnel, and one button
 *  that creates a page from it (the gallery then opens the editor). Esc and
 *  the back button close it. Opened from the New page gallery, above it. */
export default function TemplatePreviewModal<T extends PreviewTemplate>({ tpl, onClose, onUse, busy = false }: {
  tpl: T | null;
  onClose: () => void;
  onUse: (tpl: T) => void;
  /** The page is being created: the button shows it and can't be pressed twice. */
  busy?: boolean;
}) {
  // Phones open on the phone view; a scaled desktop page is unreadable there.
  const [width, setWidth] = useState<'desktop' | 'mobile'>(() => (typeof window !== 'undefined' && window.innerWidth < 768 ? 'mobile' : 'desktop'));
  const [step, setStep] = useState(0);
  const dialogRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });
  useEffect(() => { setStep(0); }, [tpl]);

  // Esc closes the preview only (the gallery under it stays open). Focus moves
  // into the preview and back to whatever opened it.
  useEffect(() => {
    if (!tpl) return;
    const back = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopImmediatePropagation();
      onCloseRef.current();
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      if (back?.isConnected && back !== document.body) back.focus({ preventScroll: true });
    };
  }, [tpl]);

  // Template images are relative (/template-assets/...): make them absolute,
  // as the page will be when it is created.
  const content = useMemo(() => (tpl ? localizeAssets(tpl.content, window.location.origin) : null), [tpl]);
  if (!tpl || !content || typeof document === 'undefined') return null;
  const steps = content.steps ?? [];
  const sections = steps.length > 1 ? steps[Math.min(step, steps.length - 1)]?.sections : undefined;
  const view = <PageView content={content} theme={tpl.theme} previewForm={tpl.previewForm ?? null} preview sections={sections} />;

  return createPortal(
    // pe-scope: the preview lives on document.body, outside the gallery.
    <div className="pe-scope">
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-label={`Preview of ${tpl.name}`} tabIndex={-1}
        className="fixed inset-0 z-[1055] flex flex-col bg-bg outline-none">
        <header className="flex min-h-14 shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b border-border bg-surface px-3 py-2 sm:px-4">
          <button type="button" className="btn-ghost btn-sm shrink-0 [@media(pointer:coarse)]:min-h-11" onClick={onClose} aria-label="Back to templates">
            <ArrowLeftIcon size={14} />Templates
          </button>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-text">{tpl.name}</div>
            {tpl.description && <div className="hidden truncate text-xs text-textDim md:block">{tpl.description}</div>}
          </div>
          {steps.length > 1 && (
            <div className="seg shrink-0" role="tablist" aria-label="Funnel step">
              {steps.map((st, i) => (
                <button key={st.id} type="button" role="tab" aria-selected={step === i}
                  className={`seg-item [@media(pointer:coarse)]:min-h-10 ${step === i ? 'seg-item-active' : ''}`} onClick={() => setStep(i)}>
                  {st.name || `Step ${i + 1}`}
                </button>
              ))}
            </div>
          )}
          <div className="seg shrink-0" role="group" aria-label="Preview size">
            {WIDTHS.map(([w, label]) => (
              <button key={w} type="button" aria-pressed={width === w}
                className={`seg-item [@media(pointer:coarse)]:min-h-10 ${width === w ? 'seg-item-active' : ''}`} onClick={() => setWidth(w)}>
                {label}
              </button>
            ))}
          </div>
          <button type="button" className="btn-primary btn-sm shrink-0 [@media(pointer:coarse)]:min-h-11" disabled={busy} onClick={() => onUse(tpl)}>
            {busy ? 'Creating…' : 'Use this template'}
          </button>
        </header>
        <div className="min-h-0 flex-1 bg-surface2 p-3 sm:p-4">
          {width === 'mobile' ? (
            <div className="mx-auto h-full w-full max-w-[400px]">
              <div className="h-full overflow-hidden rounded-[32px] border-[6px] border-border bg-bg">
                <IframePreview className="h-full w-full" title={`${tpl.name} on a phone`}>{view}</IframePreview>
              </div>
            </div>
          ) : (
            <DesktopPreview className="h-full" title={`${tpl.name} on a desktop`}>{view}</DesktopPreview>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
